import { useEffect, useRef, useState } from "react"

import { useStore } from "@/store"

import CopilotDrawer from "./CopilotDrawer"
import CopilotFloating from "./CopilotFloating"
import CopilotSurface from "./CopilotSurface"
import type { CopilotContainerRef } from "./types"
import { useCopilotDraft } from "./useCopilotDraft"

const Copilot = () => {
  const {
    isCopilotOpen: open,
    setIsCopilotOpen: setOpen,
    copilotMode: mode,
    setCopilotMode: setMode,
    copilotDrawerWidth: width,
    setCopilotDrawerWidth: setWidth,
    copilotNewChatRequestNonce,
  } = useStore((state) => ({
    isCopilotOpen: state.isCopilotOpen,
    setIsCopilotOpen: state.setIsCopilotOpen,
    copilotMode: state.copilotMode,
    setCopilotMode: state.setCopilotMode,
    copilotDrawerWidth: state.copilotDrawerWidth,
    setCopilotDrawerWidth: state.setCopilotDrawerWidth,
    copilotNewChatRequestNonce: state.copilotNewChatRequestNonce,
  }))
  const {
    draft,
    setDraft,
    isSendPending,
    beginSend,
    acceptSend,
    failSend,
    completeSend,
  } = useCopilotDraft()

  const containerRef = useRef<CopilotContainerRef | null>(null)
  const launcherRef = useRef<HTMLButtonElement | null>(null)
  const restoreFocusRef = useRef<HTMLElement | null>(null)
  const focusFrameRef = useRef<number | undefined>(undefined)
  const handledNewChatRequestNonceRef = useRef(0)
  const wasOpenRef = useRef(open)
  const [hasForegroundDialog, setHasForegroundDialog] = useState(false)

  const isDrawerMode = mode === "drawer"

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false)
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [open, setOpen])

  useEffect(() => {
    if (!open) {
      if (focusFrameRef.current) {
        cancelAnimationFrame(focusFrameRef.current)
        focusFrameRef.current = undefined
      }

      if (!wasOpenRef.current) {
        return
      }

      wasOpenRef.current = false

      const target = restoreFocusRef.current ?? launcherRef.current
      if (target) {
        target.focus()
      }
      restoreFocusRef.current = null
      return
    }

    if (!wasOpenRef.current) {
      if (document.activeElement instanceof HTMLElement) {
        restoreFocusRef.current = document.activeElement
      } else if (launcherRef.current) {
        restoreFocusRef.current = launcherRef.current
      }
    }

    wasOpenRef.current = true

    focusFrameRef.current = requestAnimationFrame(() => {
      containerRef.current?.focusInput()
    })

    return () => {
      if (focusFrameRef.current) {
        cancelAnimationFrame(focusFrameRef.current)
        focusFrameRef.current = undefined
      }
    }
  }, [mode, open])

  useEffect(() => {
    const updateForegroundDialogState = () => {
      setHasForegroundDialog(
        document.querySelector(
          '[data-slot="dialog-content"], [data-slot="alert-dialog-content"]'
        ) !== null
      )
    }

    updateForegroundDialogState()

    const observer = new MutationObserver(() => {
      updateForegroundDialogState()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state"],
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  const handleModeToggle = () => {
    setMode(isDrawerMode ? "floating" : "drawer")
  }

  const handleClose = () => {
    setOpen(false)
  }

  const pendingNewChatRequestNonce =
    copilotNewChatRequestNonce > handledNewChatRequestNonceRef.current
      ? copilotNewChatRequestNonce
      : null

  return (
    <CopilotSurface
      ref={containerRef}
      draft={draft}
      isDraftSendPending={isSendPending}
      onDraftChange={setDraft}
      onDraftSendBegin={beginSend}
      onDraftSendAccepted={acceptSend}
      onDraftSendFailed={failSend}
      onDraftSendCompleted={completeSend}
      mode={mode}
      isActive={open}
      newChatRequestNonce={pendingNewChatRequestNonce}
      onNewChatRequestHandled={(nonce) => {
        handledNewChatRequestNonceRef.current = Math.max(
          handledNewChatRequestNonceRef.current,
          nonce
        )
      }}
      onModeToggle={handleModeToggle}
      onClose={handleClose}
    >
      {(surface) =>
        isDrawerMode ? (
          <CopilotDrawer
            surface={surface}
            isOpen={open}
            width={width}
            isBackgrounded={hasForegroundDialog}
            onOpen={() => setOpen(true)}
            onWidthChange={setWidth}
            launcherRef={launcherRef}
          />
        ) : (
          <CopilotFloating
            surface={surface}
            isOpen={open}
            isBackgrounded={hasForegroundDialog}
            onOpenChange={setOpen}
            launcherRef={launcherRef}
          />
        )
      }
    </CopilotSurface>
  )
}

export default Copilot
